import {
    FileItem, QuestionnaireAttachmentProps,
} from './types'

import {
    useAppDispatch, useAppSelector,
    useAppSelectorWithShallowEquality,
} from 'hooks'

import {NinchatEvents} from 'api/types'
import QuestionnaireAttachmentView from './QuestionnaireAttachmentView'
import React from 'react'
import {UiState} from 'state/ui'
import {UiStore} from 'store/types'

import {describeFile} from 'api/action/describeFile'
import {removeUnlinkedFile} from 'api/action/removeUnlinkedFile'
import {sendUnlinkedFiles} from 'api/action/sendUnlinkedFiles'
import {toast} from 'components/Toast'
import {useTranslation} from 'react-i18next'

const QuestionnaireAttachment = ({
    hideActions = false,
    files: initialFiles,
    onContinue,
    onClose,
    onChange,
}: QuestionnaireAttachmentProps): React.ReactElement => {
    const dispatch = useAppDispatch()
    const [files, setFiles] = React.useState<FileItem[]>([])
    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const [filesState, setFilesState] = React.useState<UiStore.UiStoreAttachmentFile[] | undefined>(undefined)

    const {files: filesStateFromStore}: {files: UiStore.UiStoreAttachmentFile[] | undefined} = useAppSelectorWithShallowEquality(({ui}: {ui: UiState}) => ({files: ui.file_uploads?.[userId || '']}))

    const {t} = useTranslation()

    const prepareFiles = () => Promise.all(initialFiles.map(async (file) => {
        let fileDetails: Partial<NinchatEvents.NinchatFileFoundEvent> = {}
        let fileId = file.file_id

        if (fileId) {
            if (!file.file_attrs) {
                try {
                    fileDetails = await dispatch(describeFile({file_id: fileId})).unwrap()
                } catch (error) {
                    console.error('Error describing file:', error)
                }
            }
        } else {
            const fileState = filesState?.find((stateFile) => stateFile.name === file.name)
            fileId = fileState?.file_id
        }

        if (fileDetails.file_attrs) {
            return {
                ...file,
                converted_url: fileDetails.converted_url,
                file_attrs: fileDetails.file_attrs,
                file_id: fileId,
                file_url: fileDetails.file_url,
                thumbnail_url: fileDetails.thumbnail_url,
                url_expiry: fileDetails.url_expiry,
            }
        }
        return {
            ...file,
            file_id: fileId,
        }
    }))

    React.useEffect(() => {
        if (filesStateFromStore) {
            setFilesState(filesStateFromStore)
        }
    }, [JSON.stringify(filesStateFromStore)])

    React.useEffect(() => {
        if (initialFiles.length === 0) {
            return
        }

        prepareFiles().then((filesWithIds) => {
            setFiles(filesWithIds)
            onChange(filesWithIds)
        })
    }, [JSON.stringify(filesState), JSON.stringify(initialFiles)])

    const handleAddFiles = (fileList: FileList): void => {
        const newFiles: FileItem[] = Array.from(fileList)
            .map((file) => ({
                file,
                name: file.name,
                size: file.size,
            }))
            .filter((file) => !files.some((existingFile) => existingFile.name === file.name))

        setFiles((prevFiles) => {
            const filesToAdd = [...prevFiles, ...newFiles]
            onChange(filesToAdd)
            return filesToAdd
        })
    }

    const handleRemoveFile = (fileName: string, fileId?: string): void => {
        if (fileId) {
            dispatch(removeUnlinkedFile({file_id: fileId})).unwrap()
                .then(() => {
                    toast.success(t('File removed successfully'))
                    setFiles((prevFiles) => {
                        const filesToKeep = prevFiles.filter((file) => file.file_id !== fileId)
                        onChange(filesToKeep)
                        return filesToKeep
                    })
                }, () => {
                    toast.error(t('Error removing file'))
                    setFiles((prevFiles) => {
                        const filesToKeep = prevFiles.filter((file) => file.file_id !== fileId)
                        onChange(filesToKeep)
                        return filesToKeep
                    })
                })
        } else {
            setFiles((prevFiles) => {
                const filesToKeep = prevFiles.filter((file) => file.name !== fileName)
                onChange(filesToKeep)
                return filesToKeep
            })
        }
    }

    const handleContinue = (): void => {
        onContinue()
    }

    const handleUploadFiles = (filesToUpload: Array<string>): void => {
        const filesForUpload = files.filter((file) => filesToUpload.includes(file.name))

        filesForUpload.forEach((file) => {
            if (file.file !== undefined) {
                dispatch(sendUnlinkedFiles({
                    file: file.file,
                    file_attrs: {name: file.name},
                    user_id: userId || '',
                }))
            }
        })
    }

    const handleRetryFile = (fileName: string): void => {
        setFilesState((currFilesState) => currFilesState?.map((item) => {
            if (item.name === fileName) {
                return {
                    ...item,
                    error: undefined,
                    progress: 0,
                }
            }
            return item
        }))

        setTimeout(() => {
            const fileToRetry = files.find((file) => file.name === fileName)
            if (fileToRetry && fileToRetry.file !== undefined) {
                dispatch(sendUnlinkedFiles({
                    file: fileToRetry.file,
                    file_attrs: {name: fileToRetry.name},
                    user_id: userId || '',
                }))
            }
        }, 500)
    }

    return (
        <QuestionnaireAttachmentView
            files={files}
            filesState={filesState}
            hideActions={hideActions}
            onAddFiles={handleAddFiles}
            onClose={onClose}
            onContinue={handleContinue}
            onRemoveFile={handleRemoveFile}
            onRetryFile={handleRetryFile}
            onUploadFiles={handleUploadFiles}
        />
    )
}

export default QuestionnaireAttachment
